/**
 * Notifications — the feed the Overview page reads (`chorus-byggeplan.md` Fase 1.4, "uleste
 * notifikasjoner"; `docs/design/00-contracts.md` §6.20).
 *
 * ---------------------------------------------------------------------------------------------
 * A NOTIFICATION IS A KEY AND A BAG OF PARAMETERS. IT IS NEVER A SENTENCE.
 *
 * `notifications` stores `i18n_key` and `i18n_params` and no rendered text, and this contract
 * carries both through untouched. There is deliberately **no `message`, `title` or `body`
 * field**: the API never returns a translated sentence (CLAUDE.md, language rules; principle 8),
 * and a person who switches from Norwegian to English has to see the notifications they already
 * have in English. The web app renders `t(i18nKey, i18nParams)`; the mobile app that comes later
 * renders the same pair from its own locale files, which is only possible because the sentence
 * was never baked in here.
 *
 * `apps/api/src/notifications/i18n-keys.spec.ts` is the other half of that split: it asserts that
 * every key a listener emits exists in both locale files with exactly the placeholders the
 * listener fills. Nothing else connects the two halves, because by design nothing else can.
 * ---------------------------------------------------------------------------------------------
 *
 * WHAT THE FEED IS NOT. There is no `recipientPersonId` on the DTO. Every row this endpoint can
 * return is addressed to the caller — `r_notifications_recipient` is a RESTRICTIVE policy on
 * `recipient_person_id = app.require_person_id()` and there is no admin override by design — so
 * the field would be a constant per request whose only effect is to suggest it could be somebody
 * else. `eventId` is absent for the same kind of reason: it is a tracing column, not something a
 * client acts on.
 *
 * THE DEEP LINK IS A PAIR OR IT IS NOTHING. `objectType` and `objectId` are null together or set
 * together — `ck_notifications__object_pairing` enforces it in the database — so a client either
 * has somewhere to navigate to or renders the notification as plain text. Half a link is not a
 * state this contract can express.
 */
import { z } from 'zod';

import { objectTypeSchema } from '../enums.js';
import { cursorQuerySchema, page } from '../http/pagination.js';
import { booleanQuerySchema } from './common.js';

/**
 * The one sort the feed offers: newest first.
 *
 * Backed by `ix_notifications__org_recipient (organization_id, recipient_person_id, id DESC)`
 * and, with `?unread=true`, by `ix_notifications__unread`, the same three columns
 * `WHERE read_at IS NULL`. Ids are UUIDv7, so `id` **is** arrival order and is already unique:
 * this sort orders by one column and its cursor carries one value.
 *
 * There is deliberately no `type` or `object` sort. Neither has an index, and a four-value
 * column at the head of an `ORDER BY` is migration 0008's mistake — a page that is correctly
 * ordered and reads as unordered. Adding a sort starts with a migration.
 */
export const NOTIFICATION_SORT_KEYS = ['recent'] as const;
export type NotificationSortKey = (typeof NOTIFICATION_SORT_KEYS)[number];

/**
 * The highest number the badge ever reports; above it the client renders "99+".
 *
 * This is the whole reason an unread *count* is allowed to exist beside principle 6's "never
 * `COUNT(*)`". That prohibition is about pagination: a list that counts its own total reads the
 * whole backlog to render a number nobody scrolls to. A badge is a different question with a
 * different answer — the count is bounded before it is taken, by a `LIMIT` inside the
 * aggregate, so the query touches at most a hundred entries of `ix_notifications__unread` and
 * costs the same whether the reader has a hundred unread notifications or ten thousand.
 *
 * The response says which of the two it is, so the client never has to guess whether `99` means
 * ninety-nine or "at least ninety-nine".
 */
export const UNREAD_BADGE_MAX = 99;

/**
 * One notification.
 *
 * `i18nParams` is a flat map of strings because that is what i18next interpolates and what
 * `app.push_notification()` is ever given. It is passed through exactly as stored — not
 * rendered, not translated, not merged into a sentence.
 */
export const notificationDtoSchema = z
  .object({
    id: z.uuid(),
    /**
     * The event that produced it, e.g. `task.assigned`. Free text rather than an enum, exactly
     * as the column is: a new listener must not need a migration, and this is what a client
     * groups or filters by locally.
     */
    type: z.string(),
    /** e.g. `notifications.taskAssigned.body`. Rendered by the client, never by the API. */
    i18nKey: z.string(),
    /** e.g. `{"taskTitle":"Bestill backline","actor":"Kari"}`. */
    i18nParams: z.record(z.string(), z.string()),
    /** Deep-link target. Null together with `objectId`, or both set. */
    objectType: objectTypeSchema.nullable(),
    objectId: z.uuid().nullable(),
    /** Null = unread. The only field this API lets a client change, and only forwards. */
    readAt: z.iso.datetime().nullable(),
    createdAt: z.iso.datetime(),
  })
  .meta({ id: 'Notification' });

/**
 * The feed query.
 *
 * `direction` is a literal rather than the usual enum. A notification feed is read from the
 * newest end — that is what "uleste notifikasjoner" on the Overview page means — and ascending
 * would start a caller at the oldest notification they ever received, which is not a view this
 * API offers. Fixing it in the schema means a client that asks for it gets a 400 naming the
 * field rather than a page nobody wanted.
 */
export const listNotificationsQuerySchema = cursorQuerySchema(NOTIFICATION_SORT_KEYS)
  .extend({
    direction: z.literal('desc').default('desc'),
    /**
     * `?unread=true` narrows to `read_at IS NULL`, off `ix_notifications__unread`, whose
     * predicate is exactly that and which carries the sort as well as the filter.
     * `?unread=false` is its negation — the history — and omitting it is the whole feed.
     */
    unread: booleanQuerySchema.optional(),
  })
  .meta({ id: 'ListNotificationsQuery' });

export const notificationListResponseSchema = page(
  notificationDtoSchema,
  'NotificationListResponse',
);

/**
 * The badge. `count` is capped at `UNREAD_BADGE_MAX`; `capped` says whether the cap was reached,
 * so "99" and "99+" are distinguishable without the client inventing the distinction.
 */
export const notificationUnreadCountSchema = z
  .object({
    count: z.number().int().min(0).max(UNREAD_BADGE_MAX),
    capped: z.boolean(),
  })
  .meta({ id: 'NotificationUnreadCount' });

/**
 * What `POST /v1/notifications/read-all` reports.
 *
 * A count, and not a page of the rows it touched: the caller asked to clear the feed, and
 * returning what was cleared would be a second list nobody requested. `markedCount` is the
 * `UPDATE`'s own row count — free, and not a `COUNT(*)`.
 */
export const markNotificationsReadResponseSchema = z
  .object({ markedCount: z.number().int().min(0) })
  .meta({ id: 'MarkNotificationsReadResponse' });

export type NotificationDto = z.infer<typeof notificationDtoSchema>;
export type ListNotificationsQuery = z.infer<typeof listNotificationsQuerySchema>;
export type NotificationListResponse = z.infer<typeof notificationListResponseSchema>;
export type NotificationUnreadCount = z.infer<typeof notificationUnreadCountSchema>;
export type MarkNotificationsReadResponse = z.infer<typeof markNotificationsReadResponseSchema>;

/**
 * What `POST /v1/notifications/read-by-object` asks for (decision 0012).
 *
 * ---------------------------------------------------------------------------------------------
 * READING THE OBJECT CLEARS THE NOTIFICATION — BUT THE CLIENT SAYS SO, THE GET DOES NOT.
 *
 * Atle, 29 August: "om man har vært aktivt inne og lest det som er skjedd fjernes det fra ulest
 * varsel ... litt som i Teams". The obvious implementation is to clear notifications inside
 * `GET /tasks/:id`, and it is wrong twice over. A GET that writes is not cacheable and not
 * safely retryable — and this app PREFETCHES on hover, so a pointer crossing a list would silently
 * empty somebody's feed. Whether a person actually read something is a fact only the client holds:
 * the detail view was mounted, not merely fetched.
 *
 * So the act is its own request, and it is explicit. That also keeps the rule out of eleven
 * different services: one route clears by `(objectType, objectId)` for any object that a
 * notification can deep-link to, because the deep link is ALREADY that pair
 * (`ck_notifications__object_pairing`). There is nothing per-type to write.
 *
 * IT IS NOT `notification:list` SCOPED TO AN OBJECT. It never returns rows, only how many it
 * cleared, so a caller cannot use it to ask whether a task they cannot see has notifications
 * about it — the answer for somebody else's feed is zero either way, because
 * `r_notifications_recipient` is restrictive with no admin override.
 * ---------------------------------------------------------------------------------------------
 */
export const markNotificationsReadByObjectRequestSchema = z
  .object({
    objectType: objectTypeSchema,
    objectId: z.uuid(),
  })
  .meta({ id: 'MarkNotificationsReadByObjectRequest' });

export type MarkNotificationsReadByObjectRequest = z.infer<
  typeof markNotificationsReadByObjectRequestSchema
>;
